import clsx from 'clsx';
import { type MotionProps, motion } from 'framer-motion';
import { Fragment, type FunctionComponent, type ReactNode } from 'react';

import { Button } from '../button/Button';

type ButtonConfig = {
    readonly 'aria-label': string;
    readonly content: ReactNode;
    readonly onClick: () => void;
};

type ColorVariant = 'dark' | 'light';

export type FloatingSplitActionButtonProps = {
    readonly className?: string;
    readonly colorVariant?: ColorVariant;
    readonly leftButton: ButtonConfig;
    readonly rightButton: ButtonConfig | null;
};

export const FloatingSplitActionButton: FunctionComponent<FloatingSplitActionButtonProps & MotionProps> = ({
    animate,
    className,
    colorVariant = 'light',
    exit,
    initial,
    leftButton,
    rightButton,
    variants,
}) => {
    return (
        <motion.div
            animate={animate}
            className={clsx('rounded-xl shadow-lg', className)}
            exit={exit}
            initial={initial}
            variants={variants}
        >
            <div
                className={clsx(
                    'flex items-center rounded-xl overflow-hidden p-0 relative',
                    '[&_button:first-of-type]:rounded-r-none [&_button:last-of-type]:rounded-l-none',
                    colorVariant === 'dark'
                        ? 'bg-black-700 [&_button]:text-white-200 [&_button:hover]:bg-black-600'
                        : 'bg-white-200',
                )}
            >
                <Button aria-label={leftButton['aria-label']} variant="tertiary" onClick={leftButton.onClick}>
                    {leftButton.content}
                </Button>
                {rightButton !== null && (
                    <Fragment>
                        <div className={clsx('h-7xl w-px', colorVariant === 'dark' ? 'bg-black-500' : 'bg-gray-400')} />
                        <Button
                            aria-label={rightButton['aria-label']}
                            iconOnly={true}
                            variant="tertiary"
                            onClick={rightButton.onClick}
                        >
                            {rightButton.content}
                        </Button>
                    </Fragment>
                )}
            </div>
        </motion.div>
    );
};
